import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Avatar,
  Alert,
  AlertTitle
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon,
  TrendingUp,
  TrendingDown,
  AccountBalance,
  Home,
  CalendarToday,
  Euro,
  Person,
  Business,
  Warning,
  CheckCircle,
  Cancel
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

interface ContractInchiriere {
  id: string;
  numar: string;
  dataInceput: string;
  dataSfarsit: string;
  parohieId: string;
  parohieNume: string;
  proprietar: string;
  proprietarCUI: string;
  proprietarAdresa: string;
  proprietarTelefon: string;
  proprietarEmail: string;
  proprietarReprezentant: string;
  proprietarReprezentantFunctie: string;
  proprietarReprezentantCI: string;
  proprietarReprezentantCNP: string;
  proprietarReprezentantAdresa: string;
  proprietarReprezentantTelefon: string;
  proprietarReprezentantEmail: string;
  proprietarReprezentantSemnatura: string;
  chirias: string;
  chiriasCUI: string;
  chiriasAdresa: string;
  chiriasTelefon: string;
  chiriasEmail: string;
  chiriasReprezentant: string;
  chiriasReprezentantFunctie: string;
  chiriasReprezentantCI: string;
  chiriasReprezentantCNP: string;
  chiriasReprezentantAdresa: string;
  chiriasReprezentantTelefon: string;
  chiriasReprezentantEmail: string;
  chiriasReprezentantSemnatura: string;
  obiectContract: string;
  adresaImobil: string;
  suprafata: number;
  unitateSuprafata: string;
  valoareChirie: number;
  moneda: string;
  perioadaPlata: string;
  garantie: number;
  observatii?: string;
  status: 'activ' | 'expirat' | 'reziliat' | 'in_asteptare';
  dataSemnare: string;
  dataExpirare: string;
  dataReziliere?: string;
  motivReziliere?: string;
  documenteAnexe: string[];
  createdAt: string;
  updatedAt: string;
}

interface ContractStats {
  totalContracte: number;
  contracteActive: number;
  contracteExpirate: number;
  contracteReziliate: number;
  valoareTotalaChirii: number;
  contracteExpirandIn30Zile: number;
}

interface FacturaChirie {
  id: string;
  numar: string;
  data: string;
  contractId: string;
  contractNumar: string;
  parohieId: string;
  parohieNume: string;
  proprietar: string;
  proprietarCUI: string;
  proprietarAdresa: string;
  proprietarTelefon: string;
  proprietarEmail: string;
  proprietarReprezentant: string;
  proprietarReprezentantFunctie: string;
  proprietarReprezentantCI: string;
  proprietarReprezentantCNP: string;
  proprietarReprezentantAdresa: string;
  proprietarReprezentantTelefon: string;
  proprietarReprezentantEmail: string;
  proprietarReprezentantSemnatura: string;
  chirias: string;
  chiriasCUI: string;
  chiriasAdresa: string;
  chiriasTelefon: string;
  chiriasEmail: string;
  chiriasReprezentant: string;
  chiriasReprezentantFunctie: string;
  chiriasReprezentantCI: string;
  chiriasReprezentantCNP: string;
  chiriasReprezentantAdresa: string;
  chiriasReprezentantTelefon: string;
  chiriasReprezentantEmail: string;
  chiriasReprezentantSemnatura: string;
  obiectContract: string;
  adresaImobil: string;
  suprafata: number;
  unitateSuprafata: string;
  valoareChirie: number;
  moneda: string;
  perioadaPlata: string;
  perioadaFacturata: string;
  dataInceputPerioada: string;
  dataSfarsitPerioada: string;
  valoareChiriePerioada: number;
  tva: number;
  totalPlata: number;
  garantie: number;
  observatii?: string;
  status: 'neplatita' | 'platita' | 'anulata';
  dataScadenta: string;
  dataPlata?: string;
  metodaPlata?: 'cash' | 'transfer' | 'card' | 'cec';
  documenteAnexe: string[];
  createdAt: string;
  updatedAt: string;
}

const Contracte: React.FC = () => {
  const { hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterParohie, setFilterParohie] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedContract, setSelectedContract] = useState<ContractInchiriere | null>(null);
  const [contractDialogTab, setContractDialogTab] = useState(0);

  // Mock data pentru contracte
  const [contracte] = useState<ContractInchiriere[]>([
    {
      id: 'c1',
      numar: 'C-001/2024',
      dataInceput: '2024-01-01',
      dataSfarsit: '2024-12-31',
      parohieId: 'parohia1',
      parohieNume: 'Parohia Sf. Nicolae',
      proprietar: 'SC Imobiliare SRL',
      proprietarCUI: 'RO12345678',
      proprietarAdresa: 'Str. Principală nr. 1, București',
      proprietarTelefon: '0212345678',
      proprietarEmail: 'contact@imobiliare.ro',
      proprietarReprezentant: 'Ion Popescu',
      proprietarReprezentantFunctie: 'Administrator',
      proprietarReprezentantCI: 'CI123456',
      proprietarReprezentantCNP: '1234567890123',
      proprietarReprezentantAdresa: 'Str. Principală nr. 1, București',
      proprietarReprezentantTelefon: '0212345678',
      proprietarReprezentantEmail: 'ion.popescu@imobiliare.ro',
      proprietarReprezentantSemnatura: 'Ion Popescu',
      chirias: 'Parohia Sf. Nicolae',
      chiriasCUI: 'RO87654321',
      chiriasAdresa: 'Str. Bisericii nr. 5, București',
      chiriasTelefon: '0212345679',
      chiriasEmail: 'contact@parohiasfnicolae.ro',
      chiriasReprezentant: 'Părintele Gheorghe',
      chiriasReprezentantFunctie: 'Paroh',
      chiriasReprezentantCI: 'CI987654',
      chiriasReprezentantCNP: '9876543210987',
      chiriasReprezentantAdresa: 'Str. Bisericii nr. 5, București',
      chiriasReprezentantTelefon: '0212345679',
      chiriasReprezentantEmail: 'paroh@parohiasfnicolae.ro',
      chiriasReprezentantSemnatura: 'Părintele Gheorghe',
      obiectContract: 'Sediul parohiei - clădire cu 3 etaje',
      adresaImobil: 'Str. Principală nr. 1, București',
      suprafata: 150,
      unitateSuprafata: 'mp',
      valoareChirie: 2000,
      moneda: 'RON',
      perioadaPlata: 'lunar',
      garantie: 2000,
      observatii: 'Contract pentru sediul principal al parohiei',
      status: 'activ',
      dataSemnare: '2023-12-15',
      dataExpirare: '2024-12-31',
      documenteAnexe: ['contract_semnat.pdf', 'planuri_cladire.pdf'],
      createdAt: '2023-12-15',
      updatedAt: '2024-01-15'
    },
    {
      id: 'c2',
      numar: 'C-002/2024',
      dataInceput: '2024-02-01',
      dataSfarsit: '2025-01-31',
      parohieId: 'parohia2',
      parohieNume: 'Parohia Sf. Gheorghe',
      proprietar: 'SC Proprietati Bisericesti SRL',
      proprietarCUI: 'RO11111111',
      proprietarAdresa: 'Str. Eparhiei nr. 10, București',
      proprietarTelefon: '0211111111',
      proprietarEmail: 'contact@proprietatibisericesti.ro',
      proprietarReprezentant: 'Maria Ionescu',
      proprietarReprezentantFunctie: 'Director',
      proprietarReprezentantCI: 'CI111111',
      proprietarReprezentantCNP: '1111111111111',
      proprietarReprezentantAdresa: 'Str. Eparhiei nr. 10, București',
      proprietarReprezentantTelefon: '0211111111',
      proprietarReprezentantEmail: 'maria.ionescu@proprietatibisericesti.ro',
      proprietarReprezentantSemnatura: 'Maria Ionescu',
      chirias: 'Parohia Sf. Gheorghe',
      chiriasCUI: 'RO22222222',
      chiriasAdresa: 'Str. Sf. Gheorghe nr. 15, București',
      chiriasTelefon: '0212222222',
      chiriasEmail: 'contact@parohiasfgheorghe.ro',
      chiriasReprezentant: 'Părintele Vasile',
      chiriasReprezentantFunctie: 'Paroh',
      chiriasReprezentantCI: 'CI222222',
      chiriasReprezentantCNP: '2222222222222',
      chiriasReprezentantAdresa: 'Str. Sf. Gheorghe nr. 15, București',
      chiriasReprezentantTelefon: '0212222222',
      chiriasReprezentantEmail: 'paroh@parohiasfgheorghe.ro',
      chiriasReprezentantSemnatura: 'Părintele Vasile',
      obiectContract: 'Sediul parohiei - clădire cu 2 etaje',
      adresaImobil: 'Str. Eparhiei nr. 10, București',
      suprafata: 120,
      unitateSuprafata: 'mp',
      valoareChirie: 1500,
      moneda: 'RON',
      perioadaPlata: 'lunar',
      garantie: 1500,
      observatii: 'Contract pentru sediul secundar al parohiei',
      status: 'activ',
      dataSemnare: '2024-01-20',
      dataExpirare: '2025-01-31',
      documenteAnexe: ['contract_semnat.pdf', 'planuri_cladire.pdf', 'certificat_energetic.pdf'],
      createdAt: '2024-01-20',
      updatedAt: '2024-01-20'
    }
  ]);

  // Mock data pentru facturi de chirie
  const [facturiChirie] = useState<FacturaChirie[]>([
    {
      id: 'fc1',
      numar: 'FC-001/2024',
      data: '2024-01-01',
      contractId: 'c1',
      contractNumar: 'C-001/2024',
      parohieId: 'parohia1',
      parohieNume: 'Parohia Sf. Nicolae',
      proprietar: 'SC Imobiliare SRL',
      proprietarCUI: 'RO12345678',
      proprietarAdresa: 'Str. Principală nr. 1, București',
      proprietarTelefon: '0212345678',
      proprietarEmail: 'contact@imobiliare.ro',
      proprietarReprezentant: 'Ion Popescu',
      proprietarReprezentantFunctie: 'Administrator',
      proprietarReprezentantCI: 'CI123456',
      proprietarReprezentantCNP: '1234567890123',
      proprietarReprezentantAdresa: 'Str. Principală nr. 1, București',
      proprietarReprezentantTelefon: '0212345678',
      proprietarReprezentantEmail: 'ion.popescu@imobiliare.ro',
      proprietarReprezentantSemnatura: 'Ion Popescu',
      chirias: 'Parohia Sf. Nicolae',
      chiriasCUI: 'RO87654321',
      chiriasAdresa: 'Str. Bisericii nr. 5, București',
      chiriasTelefon: '0212345679',
      chiriasEmail: 'contact@parohiasfnicolae.ro',
      chiriasReprezentant: 'Părintele Gheorghe',
      chiriasReprezentantFunctie: 'Paroh',
      chiriasReprezentantCI: 'CI987654',
      chiriasReprezentantCNP: '9876543210987',
      chiriasReprezentantAdresa: 'Str. Bisericii nr. 5, București',
      chiriasReprezentantTelefon: '0212345679',
      chiriasReprezentantEmail: 'paroh@parohiasfnicolae.ro',
      chiriasReprezentantSemnatura: 'Părintele Gheorghe',
      obiectContract: 'Sediul parohiei - clădire cu 3 etaje',
      adresaImobil: 'Str. Principală nr. 1, București',
      suprafata: 150,
      unitateSuprafata: 'mp',
      valoareChirie: 2000,
      moneda: 'RON',
      perioadaPlata: 'lunar',
      perioadaFacturata: 'Ianuarie 2024',
      dataInceputPerioada: '2024-01-01',
      dataSfarsitPerioada: '2024-01-31',
      valoareChiriePerioada: 2000,
      tva: 380,
      totalPlata: 2380,
      garantie: 2000,
      observatii: 'Chirie pentru luna ianuarie 2024',
      status: 'platita',
      dataScadenta: '2024-01-31',
      dataPlata: '2024-01-25',
      metodaPlata: 'transfer',
      documenteAnexe: ['factura_chirie_ianuarie.pdf', 'ordine_plata.pdf'],
      createdAt: '2024-01-01',
      updatedAt: '2024-01-25'
    },
    {
      id: 'fc2',
      numar: 'FC-002/2024',
      data: '2024-02-01',
      contractId: 'c1',
      contractNumar: 'C-001/2024',
      parohieId: 'parohia1',
      parohieNume: 'Parohia Sf. Nicolae',
      proprietar: 'SC Imobiliare SRL',
      proprietarCUI: 'RO12345678',
      proprietarAdresa: 'Str. Principală nr. 1, București',
      proprietarTelefon: '0212345678',
      proprietarEmail: 'contact@imobiliare.ro',
      proprietarReprezentant: 'Ion Popescu',
      proprietarReprezentantFunctie: 'Administrator',
      proprietarReprezentantCI: 'CI123456',
      proprietarReprezentantCNP: '1234567890123',
      proprietarReprezentantAdresa: 'Str. Principală nr. 1, București',
      proprietarReprezentantTelefon: '0212345678',
      proprietarReprezentantEmail: 'ion.popescu@imobiliare.ro',
      proprietarReprezentantSemnatura: 'Ion Popescu',
      chirias: 'Parohia Sf. Nicolae',
      chiriasCUI: 'RO87654321',
      chiriasAdresa: 'Str. Bisericii nr. 5, București',
      chiriasTelefon: '0212345679',
      chiriasEmail: 'contact@parohiasfnicolae.ro',
      chiriasReprezentant: 'Părintele Gheorghe',
      chiriasReprezentantFunctie: 'Paroh',
      chiriasReprezentantCI: 'CI987654',
      chiriasReprezentantCNP: '9876543210987',
      chiriasReprezentantAdresa: 'Str. Bisericii nr. 5, București',
      chiriasReprezentantTelefon: '0212345679',
      chiriasReprezentantEmail: 'paroh@parohiasfnicolae.ro',
      chiriasReprezentantSemnatura: 'Părintele Gheorghe',
      obiectContract: 'Sediul parohiei - clădire cu 3 etaje',
      adresaImobil: 'Str. Principală nr. 1, București',
      suprafata: 150,
      unitateSuprafata: 'mp',
      valoareChirie: 2000,
      moneda: 'RON',
      perioadaPlata: 'lunar',
      perioadaFacturata: 'Februarie 2024',
      dataInceputPerioada: '2024-02-01',
      dataSfarsitPerioada: '2024-02-29',
      valoareChiriePerioada: 2000,
      tva: 380,
      totalPlata: 2380,
      garantie: 2000,
      observatii: 'Chirie pentru luna februarie 2024',
      status: 'neplatita',
      dataScadenta: '2024-02-29',
      documenteAnexe: ['factura_chirie_februarie.pdf'],
      createdAt: '2024-02-01',
      updatedAt: '2024-02-01'
    },
    {
      id: 'fc3',
      numar: 'FC-003/2024',
      data: '2024-02-01',
      contractId: 'c2',
      contractNumar: 'C-002/2024',
      parohieId: 'parohia2',
      parohieNume: 'Parohia Sf. Gheorghe',
      proprietar: 'SC Proprietati Bisericesti SRL',
      proprietarCUI: 'RO11111111',
      proprietarAdresa: 'Str. Eparhiei nr. 10, București',
      proprietarTelefon: '0211111111',
      proprietarEmail: 'contact@proprietatibisericesti.ro',
      proprietarReprezentant: 'Maria Ionescu',
      proprietarReprezentantFunctie: 'Director',
      proprietarReprezentantCI: 'CI111111',
      proprietarReprezentantCNP: '1111111111111',
      proprietarReprezentantAdresa: 'Str. Eparhiei nr. 10, București',
      proprietarReprezentantTelefon: '0211111111',
      proprietarReprezentantEmail: 'maria.ionescu@proprietatibisericesti.ro',
      proprietarReprezentantSemnatura: 'Maria Ionescu',
      chirias: 'Parohia Sf. Gheorghe',
      chiriasCUI: 'RO22222222',
      chiriasAdresa: 'Str. Sf. Gheorghe nr. 15, București',
      chiriasTelefon: '0212222222',
      chiriasEmail: 'contact@parohiasfgheorghe.ro',
      chiriasReprezentant: 'Părintele Vasile',
      chiriasReprezentantFunctie: 'Paroh',
      chiriasReprezentantCI: 'CI222222',
      chiriasReprezentantCNP: '2222222222222',
      chiriasReprezentantAdresa: 'Str. Sf. Gheorghe nr. 15, București',
      chiriasReprezentantTelefon: '0212222222',
      chiriasReprezentantEmail: 'paroh@parohiasfgheorghe.ro',
      chiriasReprezentantSemnatura: 'Părintele Vasile',
      obiectContract: 'Sediul parohiei - clădire cu 2 etaje',
      adresaImobil: 'Str. Eparhiei nr. 10, București',
      suprafata: 120,
      unitateSuprafata: 'mp',
      valoareChirie: 1500,
      moneda: 'RON',
      perioadaPlata: 'lunar',
      perioadaFacturata: 'Februarie 2024',
      dataInceputPerioada: '2024-02-01',
      dataSfarsitPerioada: '2024-02-29',
      valoareChiriePerioada: 1500,
      tva: 285,
      totalPlata: 1785,
      garantie: 1500,
      observatii: 'Chirie pentru luna februarie 2024',
      status: 'platita',
      dataScadenta: '2024-02-29',
      dataPlata: '2024-02-20',
      metodaPlata: 'transfer',
      documenteAnexe: ['factura_chirie_februarie.pdf', 'ordine_plata.pdf'],
      createdAt: '2024-02-01',
      updatedAt: '2024-02-20'
    }
  ]);

  // Calculare statistici
  const stats: ContractStats = {
    totalContracte: contracte.length,
    contracteActive: contracte.filter(c => c.status === 'activ').length,
    contracteExpirate: contracte.filter(c => c.status === 'expirat').length,
    contracteReziliate: contracte.filter(c => c.status === 'reziliat').length,
    valoareTotalaChirii: contracte
      .filter(c => c.status === 'activ')
      .reduce((sum, c) => sum + c.valoareChirie, 0),
    contracteExpirandIn30Zile: contracte.filter(c => {
      const dataExpirare = new Date(c.dataExpirare);
      const dataCurenta = new Date();
      const diferentaZile = Math.ceil((dataExpirare.getTime() - dataCurenta.getTime()) / (1000 * 60 * 60 * 24));
      return diferentaZile <= 30 && diferentaZile > 0 && c.status === 'activ';
    }).length
  };

  const parohii = [
    { id: 'parohia1', nume: 'Parohia Sf. Nicolae' },
    { id: 'parohia2', nume: 'Parohia Sf. Gheorghe' }
  ];

  const filteredContracte = contracte.filter(contract => {
    const matchesSearch = contract.numar.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         contract.proprietar.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         contract.parohieNume.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         contract.obiectContract.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || contract.status === filterStatus;
    const matchesParohie = !filterParohie || contract.parohieId === filterParohie;
    
    return matchesSearch && matchesStatus && matchesParohie;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'activ': return 'success';
      case 'expirat': return 'error';
      case 'reziliat': return 'warning';
      case 'in_asteptare': return 'info';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'activ': return <CheckCircle />;
      case 'expirat': return <Cancel />;
      case 'reziliat': return <Warning />;
      case 'in_asteptare': return <CalendarToday />;
      default: return <CalendarToday />;
    }
  };

  const getInvoiceStatusColor = (status: string) => {
    switch (status) {
      case 'platita': return 'success';
      case 'neplatita': return 'error';
      case 'anulata': return 'warning';
      default: return 'default';
    }
  };

  const getContractInvoices = (contractId: string) => {
    return facturiChirie.filter(invoice => invoice.contractId === contractId);
  };

  const getContractUnpaidBalance = (contractId: string) => {
    const unpaidInvoices = facturiChirie.filter(invoice => 
      invoice.contractId === contractId && invoice.status === 'neplatita'
    );
    return unpaidInvoices.reduce((sum, invoice) => sum + invoice.totalPlata, 0);
  };

  const handleViewContract = (contract: ContractInchiriere) => {
    setSelectedContract(contract);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedContract(null);
  };

  const TabPanel = ({ children, value, index }: { children: React.ReactNode; value: number; index: number }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Contracte de Închiriere
        </Typography>
        {hasPermission('contracte.write') && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {/* Implementare adăugare contract nou */}}
          >
            Contract nou
          </Button>
        )}
      </Box>

      {/* Statistici */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Home color="primary" sx={{ mr: 1 }} />
                <Typography color="textSecondary">
                  Total contracte
                </Typography>
              </Box>
              <Typography variant="h5" color="primary.main">
                {stats.totalContracte}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CheckCircle color="success" sx={{ mr: 1 }} />
                <Typography color="textSecondary">
                  Contracte active
                </Typography>
              </Box>
              <Typography variant="h5" color="success.main">
                {stats.contracteActive}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Cancel color="error" sx={{ mr: 1 }} />
                <Typography color="textSecondary">
                  Contracte expirate
                </Typography>
              </Box>
              <Typography variant="h5" color="error.main">
                {stats.contracteExpirate}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Warning color="warning" sx={{ mr: 1 }} />
                <Typography color="textSecondary">
                  Contracte reziliate
                </Typography>
              </Box>
              <Typography variant="h5" color="warning.main">
                {stats.contracteReziliate}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Euro color="info" sx={{ mr: 1 }} />
                <Typography color="textSecondary">
                  Valoare totală chirii
                </Typography>
              </Box>
              <Typography variant="h5" color="info.main">
                {stats.valoareTotalaChirii.toLocaleString()} RON
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CalendarToday color="secondary" sx={{ mr: 1 }} />
                <Typography color="textSecondary">
                  Expiră în 30 zile
                </Typography>
              </Box>
              <Typography variant="h5" color="secondary.main">
                {stats.contracteExpirandIn30Zile}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alerte pentru contracte care expiră */}
      {stats.contracteExpirandIn30Zile > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>Atenție!</AlertTitle>
          {stats.contracteExpirandIn30Zile} contracte expiră în următoarele 30 de zile. 
          Vă rugăm să verificați și să reînnoiți contractele necesare.
        </Alert>
      )}

      <Paper sx={{ width: '100%' }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Lista Contracte" />
          <Tab label="Contracte Expirând" />
          <Tab label="Raport Contracte" />
        </Tabs>

        {/* Tab Lista Contracte */}
        <TabPanel value={activeTab} index={0}>
          {/* Filtre */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Caută contracte..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="">Toate</MenuItem>
                    <MenuItem value="activ">Activ</MenuItem>
                    <MenuItem value="expirat">Expirat</MenuItem>
                    <MenuItem value="reziliat">Reziliat</MenuItem>
                    <MenuItem value="in_asteptare">În așteptare</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Parohie</InputLabel>
                  <Select
                    value={filterParohie}
                    onChange={(e) => setFilterParohie(e.target.value)}
                    label="Parohie"
                  >
                    <MenuItem value="">Toate</MenuItem>
                    {parohii.map(parohie => (
                      <MenuItem key={parohie.id} value={parohie.id}>{parohie.nume}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<FilterIcon />}
                  onClick={() => {
                    setSearchTerm('');
                    setFilterStatus('');
                    setFilterParohie('');
                  }}
                >
                  Resetează
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Tabel contracte */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Număr</TableCell>
                  <TableCell>Parohie</TableCell>
                  <TableCell>Proprietar</TableCell>
                  <TableCell>Obiect contract</TableCell>
                  <TableCell>Valoare chirie</TableCell>
                  <TableCell>Perioada</TableCell>
                  <TableCell>Sold</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Acțiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredContracte.map((contract) => (
                  <TableRow key={contract.id}>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {contract.numar}
                      </Typography>
                    </TableCell>
                    <TableCell>{contract.parohieNume}</TableCell>
                    <TableCell>{contract.proprietar}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {contract.obiectContract}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {contract.adresaImobil}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight="bold">
                        {contract.valoareChirie.toLocaleString()} {contract.moneda}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        / {contract.perioadaPlata}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(contract.dataInceput).toLocaleDateString('ro-RO')} - {new Date(contract.dataSfarsit).toLocaleDateString('ro-RO')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        fontWeight="bold"
                        color={getContractUnpaidBalance(contract.id) > 0 ? 'error.main' : 'success.main'}
                      >
                        {getContractUnpaidBalance(contract.id).toLocaleString()} {contract.moneda}
                      </Typography>
                      {getContractUnpaidBalance(contract.id) > 0 && (
                        <Typography variant="caption" color="error">
                          {facturiChirie.filter(inv => inv.contractId === contract.id && inv.status === 'neplatita').length} facturi neplătite
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(contract.status)}
                        label={contract.status}
                        color={getStatusColor(contract.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleViewContract(contract)}
                        color="primary"
                      >
                        <ViewIcon />
                      </IconButton>
                      {hasPermission('contracte.write') && (
                        <IconButton color="secondary">
                          <EditIcon />
                        </IconButton>
                      )}
                      <IconButton color="default">
                        <DownloadIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Tab Contracte Expirând */}
        <TabPanel value={activeTab} index={1}>
          <Typography variant="h6" gutterBottom>
            Contracte care expiră în următoarele 30 de zile
          </Typography>
          {contracte.filter(c => {
            const dataExpirare = new Date(c.dataExpirare);
            const dataCurenta = new Date();
            const diferentaZile = Math.ceil((dataExpirare.getTime() - dataCurenta.getTime()) / (1000 * 60 * 60 * 24));
            return diferentaZile <= 30 && diferentaZile > 0 && c.status === 'activ';
          }).length === 0 ? (
            <Alert severity="success">
              Nu există contracte care să expire în următoarele 30 de zile.
            </Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Număr</TableCell>
                    <TableCell>Parohie</TableCell>
                    <TableCell>Proprietar</TableCell>
                    <TableCell>Data expirării</TableCell>
                    <TableCell>Zile rămase</TableCell>
                    <TableCell>Acțiuni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contracte.filter(c => {
                    const dataExpirare = new Date(c.dataExpirare);
                    const dataCurenta = new Date();
                    const diferentaZile = Math.ceil((dataExpirare.getTime() - dataCurenta.getTime()) / (1000 * 60 * 60 * 24));
                    return diferentaZile <= 30 && diferentaZile > 0 && c.status === 'activ';
                  }).map((contract) => {
                    const dataExpirare = new Date(contract.dataExpirare);
                    const dataCurenta = new Date();
                    const diferentaZile = Math.ceil((dataExpirare.getTime() - dataCurenta.getTime()) / (1000 * 60 * 60 * 24));
                    
                    return (
                      <TableRow key={contract.id}>
                        <TableCell>{contract.numar}</TableCell>
                        <TableCell>{contract.parohieNume}</TableCell>
                        <TableCell>{contract.proprietar}</TableCell>
                        <TableCell>{new Date(contract.dataExpirare).toLocaleDateString('ro-RO')}</TableCell>
                        <TableCell>
                          <Chip
                            label={`${diferentaZile} zile`}
                            color={diferentaZile <= 7 ? 'error' : diferentaZile <= 14 ? 'warning' : 'info'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => handleViewContract(contract)}
                          >
                            Reînnoiește
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* Tab Raport Contracte */}
        <TabPanel value={activeTab} index={2}>
          <Typography variant="h6" gutterBottom>
            Raport Contracte
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Distribuție pe status
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Contracte active"
                        secondary={`${stats.contracteActive} contracte`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Cancel color="error" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Contracte expirate"
                        secondary={`${stats.contracteExpirate} contracte`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Warning color="warning" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Contracte reziliate"
                        secondary={`${stats.contracteReziliate} contracte`}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Valori financiare
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <Euro color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Valoare totală chirii lunare"
                        secondary={`${stats.valoareTotalaChirii.toLocaleString()} RON`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CalendarToday color="secondary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Contracte expirând în 30 zile"
                        secondary={`${stats.contracteExpirandIn30Zile} contracte`}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>

      {/* Dialog pentru vizualizare contract */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          Detalii contract - {selectedContract?.numar}
        </DialogTitle>
        <DialogContent>
          {selectedContract && (
            <Box>
              <Tabs value={contractDialogTab} onChange={(e, newValue) => setContractDialogTab(newValue)} sx={{ mb: 3 }}>
                <Tab label="Detalii Contract" />
                <Tab label="Facturi Chirie" />
              </Tabs>

              {contractDialogTab === 0 && (
                <Grid container spacing={3}>
              {/* Informații generale */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Informații generale
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Număr contract
                </Typography>
                <Typography variant="body1">{selectedContract.numar}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Status
                </Typography>
                <Chip
                  icon={getStatusIcon(selectedContract.status)}
                  label={selectedContract.status}
                  color={getStatusColor(selectedContract.status) as any}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Data început
                </Typography>
                <Typography variant="body1">
                  {new Date(selectedContract.dataInceput).toLocaleDateString('ro-RO')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Data sfârșit
                </Typography>
                <Typography variant="body1">
                  {new Date(selectedContract.dataSfarsit).toLocaleDateString('ro-RO')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Parohie
                </Typography>
                <Typography variant="body1">{selectedContract.parohieNume}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Valoare chirie
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {selectedContract.valoareChirie.toLocaleString()} {selectedContract.moneda} / {selectedContract.perioadaPlata}
                </Typography>
              </Grid>

              {/* Obiect contract */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  Obiect contract
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Descriere
                </Typography>
                <Typography variant="body1">{selectedContract.obiectContract}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Adresă imobil
                </Typography>
                <Typography variant="body1">{selectedContract.adresaImobil}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Suprafață
                </Typography>
                <Typography variant="body1">
                  {selectedContract.suprafata} {selectedContract.unitateSuprafata}
                </Typography>
              </Grid>

              {/* Proprietar */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  Proprietar
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Denumire
                </Typography>
                <Typography variant="body1">{selectedContract.proprietar}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  CUI
                </Typography>
                <Typography variant="body1">{selectedContract.proprietarCUI}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Adresă
                </Typography>
                <Typography variant="body1">{selectedContract.proprietarAdresa}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Telefon
                </Typography>
                <Typography variant="body1">{selectedContract.proprietarTelefon}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Email
                </Typography>
                <Typography variant="body1">{selectedContract.proprietarEmail}</Typography>
              </Grid>

              {/* Chirias */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  Chirias
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Denumire
                </Typography>
                <Typography variant="body1">{selectedContract.chirias}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  CUI
                </Typography>
                <Typography variant="body1">{selectedContract.chiriasCUI}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Adresă
                </Typography>
                <Typography variant="body1">{selectedContract.chiriasAdresa}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Telefon
                </Typography>
                <Typography variant="body1">{selectedContract.chiriasTelefon}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Email
                </Typography>
                <Typography variant="body1">{selectedContract.chiriasEmail}</Typography>
              </Grid>

              {/* Observații */}
              {selectedContract.observatii && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    Observații
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body1">{selectedContract.observatii}</Typography>
                </Grid>
              )}
                </Grid>
              )}

              {contractDialogTab === 1 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Facturi de chirie pentru contractul {selectedContract.numar}
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Număr factură</TableCell>
                          <TableCell>Data</TableCell>
                          <TableCell>Perioada facturată</TableCell>
                          <TableCell>Valoare chirie</TableCell>
                          <TableCell>TVA</TableCell>
                          <TableCell>Total plată</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Data scadență</TableCell>
                          <TableCell>Data plată</TableCell>
                          <TableCell>Metodă plată</TableCell>
                          <TableCell>Acțiuni</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {getContractInvoices(selectedContract.id).map((invoice) => (
                          <TableRow key={invoice.id}>
                            <TableCell>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {invoice.numar}
                              </Typography>
                            </TableCell>
                            <TableCell>{new Date(invoice.data).toLocaleDateString('ro-RO')}</TableCell>
                            <TableCell>{invoice.perioadaFacturata}</TableCell>
                            <TableCell>
                              <Typography fontWeight="bold">
                                {invoice.valoareChiriePerioada.toLocaleString()} {invoice.moneda}
                              </Typography>
                            </TableCell>
                            <TableCell>{invoice.tva.toLocaleString()} {invoice.moneda}</TableCell>
                            <TableCell>
                              <Typography fontWeight="bold" color="primary">
                                {invoice.totalPlata.toLocaleString()} {invoice.moneda}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={invoice.status}
                                color={getInvoiceStatusColor(invoice.status) as any}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{new Date(invoice.dataScadenta).toLocaleDateString('ro-RO')}</TableCell>
                            <TableCell>
                              {invoice.dataPlata ? new Date(invoice.dataPlata).toLocaleDateString('ro-RO') : '-'}
                            </TableCell>
                            <TableCell>
                              {invoice.metodaPlata ? (
                                <Chip
                                  label={invoice.metodaPlata}
                                  color="info"
                                  size="small"
                                />
                              ) : '-'}
                            </TableCell>
                            <TableCell>
                              <IconButton color="primary" size="small">
                                <ViewIcon />
                              </IconButton>
                              <IconButton color="secondary" size="small">
                                <EditIcon />
                              </IconButton>
                              <IconButton color="default" size="small">
                                <DownloadIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  {getContractInvoices(selectedContract.id).length === 0 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Nu există facturi de chirie pentru acest contract.
                    </Alert>
                  )}

                  {/* Statistici facturi */}
                  {getContractInvoices(selectedContract.id).length > 0 && (
                    <Grid container spacing={2} sx={{ mt: 3 }}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="h6" color="primary">
                              {getContractInvoices(selectedContract.id).length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Total facturi
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="h6" color="success.main">
                              {getContractInvoices(selectedContract.id).filter(inv => inv.status === 'platita').length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Facturi plătite
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="h6" color="error.main">
                              {getContractInvoices(selectedContract.id).filter(inv => inv.status === 'neplatita').length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Facturi neplătite
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="h6" color="info.main">
                              {getContractInvoices(selectedContract.id).reduce((sum, inv) => sum + inv.totalPlata, 0).toLocaleString()} RON
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Valoare totală
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Închide</Button>
          <Button variant="contained" startIcon={<DownloadIcon />}>
            Descarcă contract
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Contracte;
